import React, { useContext, useEffect, useMemo, useState } from "react";
import {
  Alert,
  Box,
  Button,
  Card,
  CardActions,
  CardContent,
  CardHeader,
  Checkbox,
  Chip,
  CircularProgress,
  Container,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  Grid,
  Paper,
  FormControlLabel,
  Switch,
  Tooltip,
  Stack,
  TextField,
  Typography,
} from "@mui/material";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import SaveIcon from "@mui/icons-material/Save";
import AddIcon from "@mui/icons-material/Add";
import SyncIcon from "@mui/icons-material/Sync";
import { useNavigate, useParams } from "react-router-dom";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { AuthContext } from "../AuthContext";
import { useAlert } from "../AlertContext";
import { enrichVehicle, getCustomer, getVehicle, updateVehicle } from "../api";
import { formatDateTimeLocalInput, normalizeDateTimeInput } from "../utils";
import { hasDocumentedConsent, validateConsentMetadata } from "../consent";

function formatDateTime(value) {
  if (!value) {
    return "—";
  }
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) {
    return "—";
  }
  return date.toLocaleString("de-DE", { dateStyle: "short", timeStyle: "short" });
}

function formatCustomerDisplay(customer) {
  if (!customer) {
    return "—";
  }
  if (customer.company) {
    return customer.company;
  }
  const parts = [customer.firstName, customer.lastName].filter(Boolean);
  if (parts.length === 0) {
    return "—";
  }
  return parts.join(" ");
}

function isVehicleUnverified(vehicle) {
  if (!vehicle) {
    return true;
  }
  const hasVin = Boolean(vehicle.vin);
  const hasHsnTsn = Boolean(vehicle.hsn) && Boolean(vehicle.tsn);
  return !(hasVin || hasHsnTsn);
}

const EMPTY_FORM = {
  licensePlate: "",
  vin: "",
  hsn: "",
  tsn: "",
  make: "",
  model: "",
  modelVariant: "",
  engine: "",
  ktype: "",
  firstRegDate: "",
  mileageKm: "",
  color: "",
  notes: "",
  consentGiven: false,
  consentSource: "",
  consentTimestamp: "",
  consentText: "",
  attrs: null,
};

const CONSENT_CONFIRMATION_MESSAGE = "Bitte bestätigen Sie die Einwilligung zur TecDoc-Abfrage.";

function resolveEnrichmentError(error) {
  if (!error) {
    return "Anreicherung fehlgeschlagen.";
  }
  const type = typeof error.body?.type === "string" ? error.body.type : typeof error.type === "string" ? error.type : "";
  const detail =
    typeof error.body?.detail === "string"
      ? error.body.detail
      : typeof error.message === "string"
      ? error.message
      : typeof error.body?.title === "string"
      ? error.body.title
      : "";
  const normalizedDetail = typeof detail === "string" ? detail.toLowerCase() : "";

  const typeIncludes = (needle) => (typeof type === "string" ? type.includes(needle) : false);

  if (error.status === 502 || typeIncludes("vehicle-catalog-unavailable") || normalizedDetail.includes("rate limit")) {
    return "TecDoc-Limit erreicht. Bitte versuchen Sie es später erneut.";
  }

  if (error.status === 404 || typeIncludes("vehicle-catalog-not-found") || normalizedDetail.includes("no vehicle found")) {
    return "Es wurden keine passenden TecDoc-Daten gefunden. Bitte prüfen Sie die Eingaben.";
  }

  if (
    normalizedDetail.includes("multiple") ||
    normalizedDetail.includes("mehrere") ||
    normalizedDetail.includes("more than one")
  ) {
    return "TecDoc lieferte mehrere mögliche Fahrzeuge. Bitte präzisieren Sie die Eingaben.";
  }

  if (typeIncludes("vehicle-enrichment-error") && detail) {
    return detail;
  }

  if (detail) {
    return detail;
  }

  if (error.message) {
    return error.message;
  }

  return "Anreicherung fehlgeschlagen.";
}

export default function VehicleDetailPage() {
  const { id } = useParams();
  const { token, roles, hasRole } = useContext(AuthContext);
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { showAlert } = useAlert();

  const assignedRoles = Array.isArray(roles) ? roles : roles ? [roles] : [];
  const canEdit =
    (typeof hasRole === "function" && (hasRole("ADMIN") || hasRole("DISPONENT"))) ||
    assignedRoles.includes("ADMIN") ||
    assignedRoles.includes("DISPONENT");

  const [formValues, setFormValues] = useState(EMPTY_FORM);
  const [formErrors, setFormErrors] = useState({});
  const [submitError, setSubmitError] = useState(null);

  const [vinDialogOpen, setVinDialogOpen] = useState(false);
  const [vinValue, setVinValue] = useState("");
  const [vinDialogError, setVinDialogError] = useState(null);
  const [vinFieldError, setVinFieldError] = useState(null);
  const [vinConsentChecked, setVinConsentChecked] = useState(false);

  const [hsnDialogOpen, setHsnDialogOpen] = useState(false);
  const [hsnValue, setHsnValue] = useState("");
  const [tsnValue, setTsnValue] = useState("");
  const [hsnDialogError, setHsnDialogError] = useState(null);
  const [hsnConsentChecked, setHsnConsentChecked] = useState(false);

  const [enrichBannerError, setEnrichBannerError] = useState(null);
  const [currentEnrichmentSource, setCurrentEnrichmentSource] = useState(null);
  const [enrichmentInfoMessage, setEnrichmentInfoMessage] = useState(null);

  const vehicleQuery = useQuery({
    queryKey: ["vehicle", id],
    queryFn: () => getVehicle(token, id),
    enabled: Boolean(token && id),
  });

  const vehicle = vehicleQuery.data;

  const customerQuery = useQuery({
    queryKey: ["vehicleCustomer", vehicle?.customerId],
    queryFn: () => getCustomer(token, vehicle.customerId),
    enabled: Boolean(token && vehicle?.customerId),
  });

  const customer = customerQuery.data;

  useEffect(() => {
    if (!vehicle) {
      return;
    }
    setFormValues({
      licensePlate: vehicle.licensePlate ?? "",
      vin: vehicle.vin ?? "",
      hsn: vehicle.hsn ?? "",
      tsn: vehicle.tsn ?? "",
      make: vehicle.make ?? "",
      model: vehicle.model ?? "",
      modelVariant: vehicle.modelVariant ?? "",
      engine: vehicle.engine ?? "",
      ktype: vehicle.ktype ?? "",
      firstRegDate: vehicle.firstRegDate ?? "",
      mileageKm: vehicle.mileageKm != null ? String(vehicle.mileageKm) : "",
      color: vehicle.color ?? "",
      notes: vehicle.notes ?? "",
      consentGiven: Boolean(vehicle.consentGiven),
      consentSource: vehicle.consentSource ?? "",
      consentTimestamp: formatDateTimeLocalInput(vehicle.consentTimestamp),
      consentText: vehicle.consentText ?? "",
      attrs: vehicle.attrs ?? null,
    });
    setFormErrors({});
    setSubmitError(null);
  }, [vehicle]);

  const tecdocInfo = useMemo(() => {
    if (!vehicle || !vehicle.attrs || typeof vehicle.attrs !== "object") {
      return null;
    }
    const catalog = vehicle.attrs.catalog;
    if (!catalog || typeof catalog !== "object") {
      return null;
    }
    const tecdoc = catalog.tecdoc;
    if (!tecdoc || typeof tecdoc !== "object") {
      return null;
    }
    return tecdoc;
  }, [vehicle]);

  const enrichmentLog = useMemo(() => {
    if (!vehicle || !vehicle.attrs || typeof vehicle.attrs !== "object") {
      return [];
    }
    const log = vehicle.attrs.enrichment_log;
    return Array.isArray(log) ? log : [];
  }, [vehicle]);

  const updateMutation = useMutation({
    mutationFn: (payload) => updateVehicle(token, id, payload),
    onSuccess: async () => {
      setFormErrors({});
      setSubmitError(null);
      await queryClient.invalidateQueries({ queryKey: ["vehicle", id] });
      await queryClient.invalidateQueries({ queryKey: ["vehicles"] });
      showAlert("Fahrzeugdaten wurden aktualisiert.");
    },
    onError: (err) => {
      const fieldErrors = {};
      let nonFieldError = err.message;
      if (Array.isArray(err?.validationErrors)) {
        err.validationErrors.forEach((item) => {
          if (item.field && item.field !== "vehicleRequest") {
            fieldErrors[item.field] = item.message;
          } else if (item.message) {
            nonFieldError = item.message;
          }
        });
      }
      setFormErrors(fieldErrors);
      setSubmitError(nonFieldError);
    },
  });

  const enrichmentMutation = useMutation({
    mutationFn: ({ payload }) => enrichVehicle(token, id, payload),
    onSuccess: async (data, variables) => {
      setCurrentEnrichmentSource(null);
      setVinDialogError(null);
      setVinFieldError(null);
      setHsnDialogError(null);
      setEnrichBannerError(null);
      const status = data?.status;
      if (status === "NO_CHANGES") {
        setEnrichmentInfoMessage("TecDoc hat keine neuen Daten geliefert. Bestehende Daten wurden beibehalten.");
      } else {
        setEnrichmentInfoMessage(null);
      }
      if (variables?.source === "VIN") {
        setVinDialogOpen(false);
        setVinConsentChecked(false);
      }
      if (variables?.source === "HSN") {
        setHsnDialogOpen(false);
        setHsnConsentChecked(false);
      }
      await queryClient.invalidateQueries({ queryKey: ["vehicle", id] });
      await queryClient.invalidateQueries({ queryKey: ["vehicles"] });
      const message =
        status === "NO_CHANGES" ? "TecDoc-Daten sind bereits aktuell." : "Fahrzeugdaten wurden angereichert.";
      showAlert(message);
    },
    onError: (err, variables) => {
      const message = resolveEnrichmentError(err);
      if (variables?.source === "VIN") {
        setVinDialogError(message);
        setVinFieldError(null);
      } else if (variables?.source === "HSN") {
        setHsnDialogError(message);
      } else {
        setEnrichBannerError(message);
      }
      setEnrichmentInfoMessage(null);
      setCurrentEnrichmentSource(null);
    },
  });

  const isSaving = canEdit ? updateMutation.isPending : false;
  const isEnriching = enrichmentMutation.isPending;

  const handleChange = (field) => (event) => {
    setFormValues((prev) => ({ ...prev, [field]: event.target.value }));
  };

  const handleConsentToggle = (event) => {
    const checked = event.target.checked;
    setFormValues((prev) => ({ ...prev, consentGiven: checked }));
    if (!checked) {
      setFormErrors((prev) => ({
        ...prev,
        consentSource: undefined,
        consentTimestamp: undefined,
        consentText: undefined,
      }));
    }
  };

  const handleSubmit = (event) => {
    event.preventDefault();
    if (!canEdit || !vehicle) {
      return;
    }
    setSubmitError(null);
    setFormErrors({});
    const nextErrors = validateConsentMetadata(formValues);
    const mileageNumber = formValues.mileageKm === "" ? null : Number(formValues.mileageKm);
    if (Object.keys(nextErrors).length > 0) {
      setFormErrors(nextErrors);
      setSubmitError("Bitte vervollständigen Sie alle Pflichtfelder.");
      return;
    }
    const normalizedConsentTimestamp = formValues.consentGiven
      ? normalizeDateTimeInput(formValues.consentTimestamp)
      : null;
    const payload = {
      customerId: vehicle.customerId,
      licensePlate: formValues.licensePlate || "",
      vin: formValues.vin || "",
      hsn: formValues.hsn || "",
      tsn: formValues.tsn || "",
      make: formValues.make || "",
      model: formValues.model || "",
      modelVariant: formValues.modelVariant || "",
      engine: formValues.engine || "",
      ktype: formValues.ktype || "",
      firstRegDate: formValues.firstRegDate || null,
      mileageKm: Number.isNaN(mileageNumber) ? null : mileageNumber,
      color: formValues.color || "",
      notes: formValues.notes || "",
      consentGiven: formValues.consentGiven,
      consentSource: formValues.consentGiven ? formValues.consentSource || "" : "",
      consentTimestamp: normalizedConsentTimestamp,
      consentText: formValues.consentGiven ? formValues.consentText || "" : "",
      attrs: formValues.attrs ?? null,
    };
    updateMutation.mutate(payload);
  };

  const openVinDialog = () => {
    if (!vehicle) {
      return;
    }
    if (!hasDocumentedConsent(vehicle)) {
      setEnrichBannerError("TecDoc-Anreicherungen erfordern eine dokumentierte Einwilligung.");
      return;
    }
    setVinValue(vehicle.vin ?? "");
    setVinDialogError(null);
    setVinFieldError(null);
    setVinConsentChecked(false);
    setEnrichmentInfoMessage(null);
    setCurrentEnrichmentSource(null);
    setVinDialogOpen(true);
  };

  const submitVin = () => {
    if (!vehicle || !canEdit) {
      return;
    }
    const trimmed = vinValue.trim();
    if (!trimmed) {
      setVinFieldError("Bitte geben Sie eine VIN ein.");
      setVinDialogError(null);
      return;
    }
    if (!vinConsentChecked) {
      setVinDialogError(CONSENT_CONFIRMATION_MESSAGE);
      return;
    }
    const normalizedVin = trimmed.toUpperCase();
    setVinValue(normalizedVin);
    setVinDialogError(null);
    setVinFieldError(null);
    setEnrichmentInfoMessage(null);
    setCurrentEnrichmentSource("VIN");
    const payload = { vin: normalizedVin, consent: true };
    if (vehicle.hsn) {
      payload.hsn = vehicle.hsn;
    }
    if (vehicle.tsn) {
      payload.tsn = vehicle.tsn;
    }
    enrichmentMutation.mutate({ payload, source: "VIN" });
  };

  const openHsnDialog = () => {
    if (!vehicle) {
      return;
    }
    if (!hasDocumentedConsent(vehicle)) {
      setEnrichBannerError("TecDoc-Anreicherungen erfordern eine dokumentierte Einwilligung.");
      return;
    }
    setHsnValue(vehicle.hsn ?? "");
    setTsnValue(vehicle.tsn ?? "");
    setHsnDialogError(null);
    setHsnConsentChecked(false);
    setEnrichmentInfoMessage(null);
    setCurrentEnrichmentSource(null);
    setHsnDialogOpen(true);
  };

  const submitHsnTsn = () => {
    if (!vehicle || !canEdit) {
      return;
    }
    const trimmedHsn = hsnValue.trim();
    const trimmedTsn = tsnValue.trim();
    if (!trimmedHsn || !trimmedTsn) {
      setHsnDialogError("Bitte geben Sie HSN und TSN ein.");
      return;
    }
    if (!hsnConsentChecked) {
      setHsnDialogError(CONSENT_CONFIRMATION_MESSAGE);
      return;
    }
    const normalizedHsn = trimmedHsn.toUpperCase();
    const normalizedTsn = trimmedTsn.toUpperCase();
    setHsnValue(normalizedHsn);
    setTsnValue(normalizedTsn);
    setHsnDialogError(null);
    setEnrichmentInfoMessage(null);
    setCurrentEnrichmentSource("HSN");
    enrichmentMutation.mutate({
      payload: { hsn: normalizedHsn, tsn: normalizedTsn, consent: true },
      source: "HSN",
    });
  };

  const handleTecDocRefresh = () => {
    if (!vehicle || !canEdit) {
      return;
    }
    if (!vehicle.consentGiven) {
      setEnrichBannerError("TecDoc-Anreicherungen erfordern eine dokumentierte Einwilligung.");
      return;
    }
    const payload = { consent: true };
    if (vehicle.vin) {
      payload.vin = vehicle.vin.toUpperCase();
      if (vehicle.hsn) {
        payload.hsn = vehicle.hsn.toUpperCase();
      }
      if (vehicle.tsn) {
        payload.tsn = vehicle.tsn.toUpperCase();
      }
    } else if (vehicle.hsn && vehicle.tsn) {
      payload.hsn = vehicle.hsn.toUpperCase();
      payload.tsn = vehicle.tsn.toUpperCase();
    } else {
      setEnrichBannerError("Keine VIN oder HSN/TSN hinterlegt.");
      return;
    }
    setEnrichBannerError(null);
    setEnrichmentInfoMessage(null);
    setCurrentEnrichmentSource("REFRESH");
    enrichmentMutation.mutate({ payload, source: "REFRESH" });
  };

  if (vehicleQuery.isLoading) {
    return (
      <Container maxWidth="md" sx={{ py: 6 }}>
        <Box sx={{ display: "flex", justifyContent: "center", py: 8 }}>
          <CircularProgress />
        </Box>
      </Container>
    );
  }

  if (vehicleQuery.error) {
    return (
      <Container maxWidth="md" sx={{ py: 6 }}>
        <Stack spacing={2}>
          <Button startIcon={<ArrowBackIcon />} variant="outlined" onClick={() => navigate(-1)}>
            Zurück
          </Button>
          <Alert severity="error">{vehicleQuery.error.message || "Fahrzeug konnte nicht geladen werden."}</Alert>
        </Stack>
      </Container>
    );
  }

  if (!vehicle) {
    return null;
  }

  const hasPersistedConsent = hasDocumentedConsent(vehicle);
  const effectiveConsent = formValues.consentGiven;
  const isUnverified = isVehicleUnverified(vehicle);
  const consentTooltip = hasPersistedConsent
    ? `Einwilligung dokumentiert: ${formatDateTime(vehicle.consentTimestamp)}`
    : "Keine dokumentierte Einwilligung vorhanden.";
  const canRefreshWithExistingData =
    hasPersistedConsent && (Boolean(vehicle.vin) || (Boolean(vehicle.hsn) && Boolean(vehicle.tsn)));

  return (
    <Container maxWidth="lg" sx={{ py: 4 }}>
      <Stack spacing={3}>
        <Stack direction="row" spacing={2} alignItems="center">
          <Button startIcon={<ArrowBackIcon />} variant="outlined" onClick={() => navigate(-1)}>
            Zurück
          </Button>
          <Typography variant="h4" component="h1">
            Fahrzeugdetails
          </Typography>
          <Chip
            label={isUnverified ? "UNVERIFIED" : "VERIFIED"}
            color={isUnverified ? "warning" : "success"}
            variant={isUnverified ? "outlined" : "filled"}
          />
          <Tooltip title={consentTooltip}>
            <Chip
              label={hasPersistedConsent ? "Einwilligung dokumentiert" : "Einwilligung fehlt"}
              color={hasPersistedConsent ? "success" : "default"}
              variant={hasPersistedConsent ? "filled" : "outlined"}
            />
          </Tooltip>
        </Stack>
        <Stack direction={{ xs: "column", sm: "row" }} spacing={2} alignItems={{ xs: "flex-start", sm: "center" }}>
          <Typography variant="h6">{vehicle.licensePlate || "—"}</Typography>
          <Typography variant="body2" color="text.secondary">
            Fahrzeug-ID: {vehicle.id}
          </Typography>
        </Stack>
        <Stack spacing={0.5}>
          <Stack direction="row" spacing={1} alignItems="center">
            <Typography variant="body2" color="text.secondary">
              Kunde: {customer ? formatCustomerDisplay(customer) : customerQuery.isLoading ? "Lädt…" : "—"}
            </Typography>
            {customer && (
              <Button size="small" variant="text" onClick={() => navigate(`/customers/${vehicle.customerId}`)}>
                Kundendetails
              </Button>
            )}
          </Stack>
          <Typography variant="body2" color="text.secondary">
            Letzte Aktivität: {formatDateTime(vehicle.updatedAt)}
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Einwilligung: {hasPersistedConsent ? "ja" : "nein"}
          </Typography>
          {hasPersistedConsent && (
            <Typography variant="body2" color="text.secondary">
              Quelle: {vehicle.consentSource || "—"} · Zeitpunkt: {formatDateTime(vehicle.consentTimestamp)}
            </Typography>
          )}
        </Stack>

        {!hasPersistedConsent && (
          <Alert severity="warning">
            Für TecDoc-Anreicherungen ist eine dokumentierte Einwilligung erforderlich.
          </Alert>
        )}

        {canEdit && (
          <Stack direction={{ xs: "column", sm: "row" }} spacing={2}>
            <Button
              variant="outlined"
              startIcon={<AddIcon />}
              onClick={openVinDialog}
              disabled={!hasPersistedConsent || isEnriching}
            >
              VIN hinzufügen
            </Button>
            <Button
              variant="outlined"
              startIcon={<AddIcon />}
              onClick={openHsnDialog}
              disabled={!hasPersistedConsent || isEnriching}
            >
              HSN/TSN hinzufügen
            </Button>
            <Button
              variant="contained"
              startIcon={currentEnrichmentSource === "REFRESH" && isEnriching ? <CircularProgress size={18} color="inherit" /> : <SyncIcon />}
              onClick={handleTecDocRefresh}
              disabled={!canRefreshWithExistingData || isEnriching}
            >
              TecDoc Daten aktualisieren
            </Button>
          </Stack>
        )}
        {enrichBannerError && (
          <Alert severity="error">{enrichBannerError}</Alert>
        )}
        {enrichmentInfoMessage && (
          <Alert severity="info">{enrichmentInfoMessage}</Alert>
        )}

        <Card component={canEdit ? "form" : "div"} onSubmit={canEdit ? handleSubmit : undefined}>
          <CardHeader title="Stammdaten" subheader={`Zuletzt aktualisiert: ${formatDateTime(vehicle.updatedAt)}`} />
          <CardContent>
            {!canEdit && (
              <Box sx={{ mb: 3 }}>
                <Alert severity="info">Sie können Fahrzeugdaten nur lesen.</Alert>
              </Box>
            )}
            {canEdit && effectiveConsent && !hasPersistedConsent && (
              <Box sx={{ mb: 3 }}>
                <Alert severity="info">
                  Speichern Sie die Einwilligung, bevor Sie TecDoc-Daten anreichern.
                </Alert>
              </Box>
            )}
            <Grid container spacing={2}>
              <Grid item xs={12} sm={6} md={4}>
                <TextField
                  label="Kennzeichen"
                  value={formValues.licensePlate}
                  onChange={handleChange("licensePlate")}
                  fullWidth
                  disabled={!canEdit}
                  error={canEdit && Boolean(formErrors.licensePlate)}
                  helperText={canEdit ? formErrors.licensePlate || "" : ""}
                />
              </Grid>
              <Grid item xs={12} sm={6} md={4}>
                <TextField
                  label="VIN"
                  value={formValues.vin}
                  onChange={handleChange("vin")}
                  fullWidth
                  disabled={!canEdit}
                  error={canEdit && Boolean(formErrors.vin)}
                  helperText={canEdit ? formErrors.vin || "" : ""}
                />
              </Grid>
              <Grid item xs={12} sm={3} md={2}>
                <TextField
                  label="HSN"
                  value={formValues.hsn}
                  onChange={handleChange("hsn")}
                  fullWidth
                  disabled={!canEdit}
                  error={canEdit && Boolean(formErrors.hsn)}
                  helperText={canEdit ? formErrors.hsn || "" : ""}
                />
              </Grid>
              <Grid item xs={12} sm={3} md={2}>
                <TextField
                  label="TSN"
                  value={formValues.tsn}
                  onChange={handleChange("tsn")}
                  fullWidth
                  disabled={!canEdit}
                  error={canEdit && Boolean(formErrors.tsn)}
                  helperText={canEdit ? formErrors.tsn || "" : ""}
                />
              </Grid>
              <Grid item xs={12} sm={6} md={4}>
                <TextField
                  label="Marke"
                  value={formValues.make}
                  onChange={handleChange("make")}
                  fullWidth
                  disabled={!canEdit}
                  error={canEdit && Boolean(formErrors.make)}
                  helperText={canEdit ? formErrors.make || "" : ""}
                />
              </Grid>
              <Grid item xs={12} sm={6} md={4}>
                <TextField
                  label="Modell"
                  value={formValues.model}
                  onChange={handleChange("model")}
                  fullWidth
                  disabled={!canEdit}
                  error={canEdit && Boolean(formErrors.model)}
                  helperText={canEdit ? formErrors.model || "" : ""}
                />
              </Grid>
              <Grid item xs={12} sm={6} md={4}>
                <TextField
                  label="Variante"
                  value={formValues.modelVariant}
                  onChange={handleChange("modelVariant")}
                  fullWidth
                  disabled={!canEdit}
                  error={canEdit && Boolean(formErrors.modelVariant)}
                  helperText={canEdit ? formErrors.modelVariant || "" : ""}
                />
              </Grid>
              <Grid item xs={12} sm={6} md={4}>
                <TextField
                  label="Motor"
                  value={formValues.engine}
                  onChange={handleChange("engine")}
                  fullWidth
                  disabled={!canEdit}
                  error={canEdit && Boolean(formErrors.engine)}
                  helperText={canEdit ? formErrors.engine || "" : ""}
                />
              </Grid>
              <Grid item xs={12} sm={6} md={4}>
                <TextField
                  label="K-Type"
                  value={formValues.ktype}
                  onChange={handleChange("ktype")}
                  fullWidth
                  disabled={!canEdit}
                  error={canEdit && Boolean(formErrors.ktype)}
                  helperText={canEdit ? formErrors.ktype || "" : ""}
                />
              </Grid>
              <Grid item xs={12} sm={6} md={4}>
                <TextField
                  label="Erstzulassung"
                  type="date"
                  value={formValues.firstRegDate}
                  onChange={handleChange("firstRegDate")}
                  fullWidth
                  disabled={!canEdit}
                  InputLabelProps={{ shrink: true }}
                  error={canEdit && Boolean(formErrors.firstRegDate)}
                  helperText={canEdit ? formErrors.firstRegDate || "" : ""}
                />
              </Grid>
              <Grid item xs={12} sm={6} md={4}>
                <TextField
                  label="Kilometerstand"
                  type="number"
                  value={formValues.mileageKm}
                  onChange={handleChange("mileageKm")}
                  fullWidth
                  disabled={!canEdit}
                  error={canEdit && Boolean(formErrors.mileageKm)}
                  helperText={canEdit ? formErrors.mileageKm || "" : ""}
                />
              </Grid>
              <Grid item xs={12} sm={6} md={4}>
                <TextField
                  label="Farbe"
                  value={formValues.color}
                  onChange={handleChange("color")}
                  fullWidth
                  disabled={!canEdit}
                  error={canEdit && Boolean(formErrors.color)}
                  helperText={canEdit ? formErrors.color || "" : ""}
                />
              </Grid>
              <Grid item xs={12}>
                <TextField
                  label="Notizen"
                  value={formValues.notes}
                  onChange={handleChange("notes")}
                  fullWidth
                  multiline
                  minRows={3}
                  disabled={!canEdit}
                  error={canEdit && Boolean(formErrors.notes)}
                  helperText={canEdit ? formErrors.notes || "" : ""}
                />
              </Grid>
              <Grid item xs={12}>
                <FormControlLabel
                  control={
                    <Switch
                      checked={formValues.consentGiven}
                      onChange={handleConsentToggle}
                      disabled={!canEdit}
                    />
                  }
                  label="Einwilligung zur TecDoc-Datenverarbeitung dokumentiert"
                />
              </Grid>
              <Grid item xs={12} sm={6} md={4}>
                <TextField
                  label="Quelle der Einwilligung"
                  value={formValues.consentSource}
                  onChange={handleChange("consentSource")}
                  fullWidth
                  disabled={!canEdit || !formValues.consentGiven}
                  error={canEdit && Boolean(formErrors.consentSource)}
                  helperText={canEdit ? formErrors.consentSource || "" : ""}
                />
              </Grid>
              <Grid item xs={12} sm={6} md={4}>
                <TextField
                  label="Zeitpunkt der Einwilligung"
                  type="datetime-local"
                  value={formValues.consentTimestamp}
                  onChange={handleChange("consentTimestamp")}
                  fullWidth
                  disabled={!canEdit || !formValues.consentGiven}
                  InputLabelProps={{ shrink: true }}
                  error={canEdit && Boolean(formErrors.consentTimestamp)}
                  helperText={canEdit ? formErrors.consentTimestamp || "" : ""}
                />
              </Grid>
              <Grid item xs={12} md={4}>
                <TextField
                  label="Textbaustein"
                  value={formValues.consentText}
                  onChange={handleChange("consentText")}
                  fullWidth
                  multiline
                  minRows={2}
                  disabled={!canEdit || !formValues.consentGiven}
                  error={canEdit && Boolean(formErrors.consentText)}
                  helperText={canEdit ? formErrors.consentText || "" : ""}
                />
              </Grid>
            </Grid>
            {canEdit && submitError && (
              <Box sx={{ mt: 3 }}>
                <Alert severity="error">{submitError}</Alert>
              </Box>
            )}
          </CardContent>
          {canEdit && (
            <CardActions sx={{ justifyContent: "flex-end", px: 3, pb: 3 }}>
              <Button
                type="submit"
                variant="contained"
                startIcon={isSaving ? <CircularProgress size={18} color="inherit" /> : <SaveIcon />}
                disabled={isSaving}
              >
                {isSaving ? "Speichern…" : "Speichern"}
              </Button>
            </CardActions>
          )}
        </Card>

        <Paper sx={{ p: 3 }}>
          <Stack spacing={2}>
            <Typography variant="h6">TecDoc Daten</Typography>
            {!tecdocInfo ? (
              <Typography variant="body2" color="text.secondary">
                Keine TecDoc Daten vorhanden.
              </Typography>
            ) : (
              <Grid container spacing={2}>
                <Grid item xs={12} sm={6} md={4}>
                  <Typography variant="body2" color="text.secondary">
                    Quelle
                  </Typography>
                  <Typography variant="body1">{tecdocInfo.source || "—"}</Typography>
                </Grid>
                <Grid item xs={12} sm={6} md={4}>
                  <Typography variant="body2" color="text.secondary">
                    Lookup
                  </Typography>
                  <Typography variant="body1">{tecdocInfo.lookupType || "—"}</Typography>
                </Grid>
                <Grid item xs={12} sm={6} md={4}>
                  <Typography variant="body2" color="text.secondary">
                    TecDoc VIN
                  </Typography>
                  <Typography variant="body1">{tecdocInfo.vin || "—"}</Typography>
                </Grid>
                <Grid item xs={12} sm={6} md={4}>
                  <Typography variant="body2" color="text.secondary">
                    TecDoc HSN/TSN
                  </Typography>
                  <Typography variant="body1">
                    {tecdocInfo.hsn || "—"}/{tecdocInfo.tsn || "—"}
                  </Typography>
                </Grid>
                <Grid item xs={12} sm={6} md={4}>
                  <Typography variant="body2" color="text.secondary">
                    K-Type
                  </Typography>
                  <Typography variant="body1">{tecdocInfo.ktype || "—"}</Typography>
                </Grid>
                <Grid item xs={12} sm={6} md={4}>
                  <Typography variant="body2" color="text.secondary">
                    Motor
                  </Typography>
                  <Typography variant="body1">{tecdocInfo.engine || "—"}</Typography>
                </Grid>
              </Grid>
            )}
            {enrichmentLog.length > 0 && (
              <Stack spacing={1}>
                <Typography variant="subtitle1">Anreicherungs-Historie</Typography>
                {enrichmentLog.map((entry, index) => (
                  <Box
                    key={`${entry.timestamp ?? index}-${index}`}
                    sx={{
                      border: "1px solid",
                      borderColor: "divider",
                      borderRadius: 1,
                      p: 2,
                    }}
                  >
                    <Typography variant="body2" color="text.secondary">
                      {formatDateTime(entry.timestamp)} – {entry.source || "Unbekannte Quelle"} ({
                        entry.lookupType || "—"
                      })
                    </Typography>
                    <Typography variant="body2">
                      VIN: {entry.vin || "—"} · HSN: {entry.hsn || "—"} · TSN: {entry.tsn || "—"} · K-Type: {entry.ktype || "—"}
                    </Typography>
                    {entry.consent != null && (
                      <Typography variant="body2" color="text.secondary">
                        Einwilligung: {entry.consent ? "ja" : "nein"}
                      </Typography>
                    )}
                  </Box>
                ))}
              </Stack>
            )}
          </Stack>
        </Paper>
      </Stack>

      <Dialog
        open={vinDialogOpen}
        onClose={() => {
          if (isEnriching && currentEnrichmentSource === "VIN") {
            return;
          }
          setVinDialogOpen(false);
          setVinDialogError(null);
          setVinFieldError(null);
          setVinConsentChecked(false);
        }}
      >
        <DialogTitle>VIN hinzufügen</DialogTitle>
        <DialogContent>
          <DialogContentText>
            Bitte geben Sie die vollständige Fahrzeug-Identifizierungsnummer (17 Stellen) ein.
          </DialogContentText>
          <TextField
            autoFocus
            margin="dense"
            label="VIN"
            value={vinValue}
            onChange={(event) => {
              setVinValue(event.target.value);
              if (vinFieldError) {
                setVinFieldError(null);
              }
              if (vinDialogError) {
                setVinDialogError(null);
              }
            }}
            fullWidth
            inputProps={{ maxLength: 17 }}
            error={Boolean(vinFieldError)}
            helperText={vinFieldError || ""}
          />
          <FormControlLabel
            sx={{ mt: 1 }}
            control={
              <Checkbox
                checked={vinConsentChecked}
                onChange={(event) => {
                  setVinConsentChecked(event.target.checked);
                  if (event.target.checked && vinDialogError === CONSENT_CONFIRMATION_MESSAGE) {
                    setVinDialogError(null);
                  }
                }}
              />
            }
            label="Einwilligung zur TecDoc-Abfrage bestätigt"
          />
          {vinDialogError && (
            <Alert severity="error" sx={{ mt: 2 }}>
              {vinDialogError}
            </Alert>
          )}
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => {
              if (isEnriching && currentEnrichmentSource === "VIN") {
                return;
              }
              setVinDialogOpen(false);
              setVinDialogError(null);
              setVinFieldError(null);
              setVinConsentChecked(false);
            }}
          >
            Abbrechen
          </Button>
          <Button
            onClick={submitVin}
            disabled={!vinConsentChecked || (isEnriching && currentEnrichmentSource === "VIN")}
            variant="contained"
          >
            {currentEnrichmentSource === "VIN" && isEnriching ? "Speichert…" : "Übernehmen"}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog
        open={hsnDialogOpen}
        onClose={() => {
          if (isEnriching && currentEnrichmentSource === "HSN") {
            return;
          }
          setHsnDialogOpen(false);
          setHsnDialogError(null);
          setHsnConsentChecked(false);
        }}
      >
        <DialogTitle>HSN/TSN hinzufügen</DialogTitle>
        <DialogContent>
          <DialogContentText>
            Ergänzen Sie Herstellerschlüsselnummer (HSN) und Typschlüsselnummer (TSN), um TecDoc-Daten zu laden.
          </DialogContentText>
          <Stack direction={{ xs: "column", sm: "row" }} spacing={2} sx={{ mt: 1 }}>
            <TextField
              label="HSN"
              value={hsnValue}
              onChange={(event) => {
                setHsnValue(event.target.value);
                if (hsnDialogError && hsnDialogError !== CONSENT_CONFIRMATION_MESSAGE) {
                  setHsnDialogError(null);
                }
              }}
              inputProps={{ maxLength: 4 }}
              fullWidth
            />
            <TextField
              label="TSN"
              value={tsnValue}
              onChange={(event) => {
                setTsnValue(event.target.value);
                if (hsnDialogError && hsnDialogError !== CONSENT_CONFIRMATION_MESSAGE) {
                  setHsnDialogError(null);
                }
              }}
              inputProps={{ maxLength: 8 }}
              fullWidth
            />
          </Stack>
          <FormControlLabel
            sx={{ mt: 1 }}
            control={
              <Checkbox
                checked={hsnConsentChecked}
                onChange={(event) => {
                  setHsnConsentChecked(event.target.checked);
                  if (event.target.checked && hsnDialogError === CONSENT_CONFIRMATION_MESSAGE) {
                    setHsnDialogError(null);
                  }
                }}
              />
            }
            label="Einwilligung zur TecDoc-Abfrage bestätigt"
          />
          {hsnDialogError && (
            <Alert severity="error" sx={{ mt: 2 }}>
              {hsnDialogError}
            </Alert>
          )}
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => {
              if (isEnriching && currentEnrichmentSource === "HSN") {
                return;
              }
              setHsnDialogOpen(false);
              setHsnDialogError(null);
              setHsnConsentChecked(false);
            }}
          >
            Abbrechen
          </Button>
          <Button
            onClick={submitHsnTsn}
            disabled={!hsnConsentChecked || (isEnriching && currentEnrichmentSource === "HSN")}
            variant="contained"
          >
            {currentEnrichmentSource === "HSN" && isEnriching ? "Speichert…" : "Übernehmen"}
          </Button>
        </DialogActions>
      </Dialog>
    </Container>
  );
}
